import { Checkbox, Select } from "antd";
import _ from "lodash";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@components/Button";
import ErrorValidate from "@components/ErrorValidate";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import { COLOR_DARK, COLOR_GREEN, COLOR_WHITE } from "@static/COLORS";
import { fetchAPI } from "@utils/https/fetchAPI";
import { VALIDATE_RULES } from "@validate/index";

const FormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const GroupInput = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const FormEdit = ({
  setDataList = () => undefined,
  setIsModalVisible,
  selectedData,
  setSelectedData,
  roleOptions = [],
  customerOptions = [],
  userProfile,
}) => {
  const id = _.get(selectedData, ["id"]);
  const email = _.get(selectedData, ["email"]);
  const username = _.get(selectedData, ["username"]);
  const category_customer_id = _.get(selectedData, ["category_customer_id"]);
  const role_id = _.get(selectedData, ["role_id"]);
  const user_type = _.get(userProfile, ["user_type"]);
  const [isHighestLevel, setIsHighestLevel] = useState(
    _.isNil(category_customer_id)
  );

  const [selectedCustomer, setSelectedCustomer] =
    useState(category_customer_id);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      username,
      email,
      role_id,
      category_customer_id,
    },
  });

  const onHandleEdit = async (data) => {
    const username = _.get(data, ["username"]);
    const password = _.get(data, ["password"]);
    const email = _.get(data, ["email"]);
    const role_id = _.get(data, ["role_id"]);
    const category_customer_id = _.get(data, ["category_customer_id"], null);
    const user_type = _.isNil(category_customer_id) ? "OWNER" : "CUSTOMER";

    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/user-update",
      isAuth: true,
      data: {
        id,
        username,
        password,
        email,
        role_id,
        user_type,
        category_customer_id,
      },
    });
    if (_.get(resp, ["status"])) {
      const results = _.chain(resp).get(["result"]).value();
      const updateData = _.chain(results)
        .thru((item) => {
          return item;
        })
        .value();
      toast.success("Update User Success.");
      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
      setSelectedData(null);
      setDataList((prev) => {
        const updateDataList = _.map(prev, (item) => {
          return _.get(item, ["id"]) ===
            _.chain(results).get(["id"]).toInteger().value()
            ? { ...item, ...updateData }
            : item;
        });
        return updateDataList;
      });
    }
  };

  const filteredRoleOptions = _.filter(roleOptions, (role) => {
    return _.get(role, ["category_customer_id"]) === null;
  });

  const onHandleCustomerChange = (value) => {
    setValue("role_id", null);
    setValue("category_customer_id", value);
    setSelectedCustomer(value);
  };

  const onHandleHighestChange = (checked) => {
    setIsHighestLevel(checked);
    if (checked) {
      setValue("category_customer_id", null);
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onHandleEdit)} autoComplete="off">
      <Line />
      <InnerContainer>
        <div>
          <Controller
            name="username"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Username
                </Text>
                <StyledInput
                  disabled
                  label="username"
                  {...field}
                  size="large"
                  placeholder="username"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Password
                </Text>
                <StyledInput
                  label="password"
                  {...field}
                  size="large"
                  placeholder="password"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={_.get(VALIDATE_RULES, ["email"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Email
                </Text>
                <StyledInput
                  label="email"
                  {...field}
                  size="large"
                  placeholder="email"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          {user_type !== "CUSTOMER" && (
            <GroupInput>
              <Controller
                disabled={isHighestLevel}
                name="category_customer_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Text
                      color={COLOR_DARK}
                      fontSize="14px"
                      style={{ marginBottom: 4, marginLeft: 4 }}
                    >
                      Customer
                    </Text>
                    <Select
                      allowClear
                      {...field}
                      size="large"
                      showSearch
                      placeholder="Select a customer"
                      optionFilterProp="children"
                      style={{ width: "100%" }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").includes(input)
                      }
                      filterSort={(optionA, optionB) =>
                        (optionA?.label ?? "")
                          .toLowerCase()
                          .localeCompare((optionB?.label ?? "").toLowerCase())
                      }
                      options={customerOptions}
                      onChange={(value) => {
                        onHandleCustomerChange(value);
                        field.onChange(value);
                      }}
                      onClear={() => {
                        setSelectedCustomer(null);
                        setValue("category_customer_id", null);
                        field.onChange(null);
                      }}
                      defaultValue={category_customer_id}
                    />
                    <ErrorValidate justify="start" error={error} />
                  </div>
                )}
              />
              <CheckboxContainer>
                <Text color={COLOR_DARK} fontSize="14px">
                  Get LED
                </Text>
                <Checkbox
                  style={{ marginTop: 10 }}
                  checked={isHighestLevel}
                  onChange={(e) => onHandleHighestChange(e.target.checked)}
                />
              </CheckboxContainer>
            </GroupInput>
          )}

          <Controller
            name="role_id"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Role
                </Text>
                <Select
                  {...field}
                  size="large"
                  showSearch
                  placeholder="Select a role"
                  optionFilterProp="children"
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={filteredRoleOptions}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
        </div>

        <ButtonContainer>
          <Button
            type="submit"
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Save
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </FormContainer>
  );
};
